import { ReactNode } from "react";
import { Roles } from "../../app/features/auth/authTypes";

import CalculateIcon from "@mui/icons-material/Calculate";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import ColorizeIcon from "@mui/icons-material/Colorize";
import DiamondIcon from "@mui/icons-material/Diamond";
import GestureIcon from "@mui/icons-material/Gesture";
import GradingIcon from "@mui/icons-material/Grading";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PercentIcon from "@mui/icons-material/Percent";
import PersonIcon from "@mui/icons-material/Person";
import PictureInPictureIcon from "@mui/icons-material/PictureInPicture";
import PublicIcon from "@mui/icons-material/Public";
import TimelineIcon from "@mui/icons-material/Timeline";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import VisualPicker from "../analytics/VisualPicker";
import SimilarStone from "../analytics/SimilarStone";
import AnalysisFromDiamond from "../analytics/AnalysisFromDiamond";
import AnalysisFromImage from "../analytics/AnalysisFromImage";
import ColorAnalysis from "../analytics/ColorAnalysis";
import { DataQuality } from "../analytics/DataQuality";
import DataQualityTopic from "../analytics/DataQualityTopic";
import DiamondPricing from "../analytics/DiamondPricing";
import GradingAnalysis from "../analytics/GradingAnalysis";
import PriceAlert from "../analytics/PriceAlert";
import RegradingAlert from "../analytics/RegradingAlert";
import PricingReport from "../analytics/PricingComputation";
import PricingEstimator from "../analytics/PricingEstimator";
import PricingTrends from "../analytics/PricingTrends";
import { Fantasy, FullStock } from "../analytics/Stock";
import LearningBoxes from "../learning/LearningBoxes";
import LearningDetours from "../learning/LearningDetours";
import BoxEdit from "../learning/BoxEdit";
import DetourEdit from "../learning/DetourEdit";
import Tasks from "../management/Tasks";
import Users from "../management/Users";
import Colors from "../management/colors";
import TagSettings from "../management/TagSettings";
import MyStones from "../portfolio/MyStones";
import UnderConstruction from "../widgets/UnderConstruction";
import Welcome from "../widgets/Welcome";
import PricingManagement from "../management/PricingManagement";

type MenuItem = {
  label: string;
  icon: ReactNode;
  path: string;
  roles: Roles[];
  // A page still in soft launch: shown only to developers, on top of the role check. Not a
  // security boundary — the backend serves the page's data to the role alone.
  developerOnly?: boolean;
  component: ReactNode;
};

// Whether a page is shown: its role, and for a page in soft launch, a developer too.
export const isPageShown = (
  item: { roles: Roles[]; developerOnly?: boolean },
  role: Roles,
  isDeveloper: boolean,
) => item.roles.includes(role) && (!item.developerOnly || isDeveloper);

type Sections =
  | "System Management"
  | "Stock"
  | "Pricing"
  | "Color"
  | "Alerts"
  | "Portfolio"
  | "Setup"
  | "NoMenu";

type MenuBase = Partial<Record<Sections, MenuItem[]>>;

const menuConfig: MenuBase = {
  "System Management": [
    {
      label: "Users",
      icon: <PersonIcon />,
      path: "/management/users",
      roles: ["Admin"],
      component: <Users />,
    },
    {
      label: "Tasks",
      icon: <PendingActionsIcon />,
      path: "/tasks",
      roles: ["Admin", "Readonly"],
      component: <Tasks />,
    },
    {
      label: "Box learning",
      icon: <PictureInPictureIcon />,
      path: "/learning/box",
      roles: ["Admin", "Readonly"],
      component: <LearningBoxes />,
    },
    {
      label: "Detour learning",
      icon: <GestureIcon />,
      path: "/learning/detour",
      roles: ["Admin", "Readonly"],
      component: <LearningDetours />,
    },
    {
      label: "Pricing settings",
      icon: <MonetizationOnIcon />,
      path: "/management/pricing",
      roles: ["Admin", "Readonly"],
      component: <PricingManagement />,
    },
    {
      label: "Color settings",
      icon: <ColorLensIcon />,
      path: "/management/colors",
      roles: ["Admin", "Readonly"],
      component: <Colors />,
    },
    {
      label: "Tag settings",
      icon: <LocalOfferIcon />,
      path: "/management/tags",
      roles: ["Admin"],
      developerOnly: true,
      component: <TagSettings />,
    },
  ],
  Stock: [
    {
      label: "Langerman stock",
      icon: <WarehouseIcon />,
      path: "/analytics/fantasy",
      roles: ["Admin", "Expert", "Readonly"],
      component: <Fantasy />,
    },
    {
      label: "Full stock",
      icon: <PublicIcon />,
      path: "/analytics/fullstock",
      roles: ["Admin", "Readonly"],
      component: <FullStock />,
    },
    {
      label: "Visual picker",
      icon: <ColorizeIcon />,
      path: "/analytics/visualpicker",
      roles: ["Admin", "Expert", "Readonly"],
      component: <VisualPicker />,
    },
  ],
  Pricing: [
    {
      label: "Pricing by color",
      icon: <TimelineIcon />,
      path: "/analytics/estimator",
      roles: ["Admin", "Expert", "Readonly"],
      component: <PricingReport />,
    },
    {
      label: "Pricing estimator",
      icon: <CalculateIcon />,
      path: "/analytics/pricing/estimator",
      roles: ["Admin", "Expert", "Readonly"],
      component: <PricingEstimator />,
    },
    {
      label: "Pricing trends",
      icon: <PercentIcon />,
      path: "/analytics/pricing/trends",
      roles: ["Admin", "Readonly"],
      developerOnly: true,
      component: <PricingTrends />,
    },
  ],
  Color: [
    {
      label: "Color analysis",
      icon: <ZoomInIcon />,
      path: "/analytics/coloranalysis",
      roles: ["Admin", "Readonly"],
      developerOnly: true,
      component: <ColorAnalysis />,
    },
  ],
  Alerts: [
    {
      label: "Pricing alerts",
      icon: <WarehouseIcon />,
      path: "/alerts/pricing",
      roles: ["Admin", "Expert", "Readonly"],
      component: <PriceAlert />,
    },
    {
      label: "Regrading alerts",
      icon: <LeaderboardIcon />,
      path: "/alerts/regrading",
      // As the grading analysis page, which it leads to.
      roles: ["Admin", "Readonly"],
      developerOnly: true,
      component: <RegradingAlert />,
    },
    {
      label: "Data quality alerts",
      icon: <GradingIcon />,
      path: "/alerts/dataquality",
      roles: ["Admin", "Expert", "Readonly"],
      component: <DataQuality />,
    },
  ],
  Portfolio: [
    {
      label: "My stones",
      icon: <DiamondIcon />,
      path: "/portfolio/my-stones",
      roles: ["User"],
      component: <MyStones />,
    },
  ],
  NoMenu: [
    {
      label: "",
      icon: null,
      path: "/",
      roles: ["Admin", "Expert", "User", "Readonly"],
      component: <Welcome />,
    },
    {
      label: "",
      icon: null,
      path: "/construct",
      roles: ["Admin", "Expert", "User", "Readonly"],
      component: <UnderConstruction />,
    },
    {
      label: "",
      icon: null,
      path: "/analytics/fromimage/:id",
      roles: ["Admin", "Expert", "Readonly"],
      component: <AnalysisFromImage />,
    },
    {
      label: "",
      icon: null,
      path: "/analytics/fromdiamond/:id",
      roles: ["Admin", "Expert", "Readonly"],
      component: <AnalysisFromDiamond />,
    },
    {
      // Reached from Find similar on a stone's analysis, never from the menu.
      label: "",
      icon: null,
      path: "/analytics/similar/:id",
      roles: ["Admin", "Expert", "Readonly"],
      component: <SimilarStone />,
    },
    {
      label: "",
      icon: null,
      path: "/analytics/diamondpricing/:id",
      roles: ["Admin", "Expert", "Readonly"],
      component: <DiamondPricing />,
    },
    {
      label: "",
      icon: null,
      path: "/analytics/grading/:id",
      // As seesGrading: the page reads the color spreads, which only these roles may see.
      roles: ["Admin", "Readonly"],
      developerOnly: true,
      component: <GradingAnalysis />,
    },
    {
      label: "",
      icon: null,
      path: "/learning/box/edit/:id",
      roles: ["Admin"],
      component: <BoxEdit />,
    },
    {
      label: "",
      icon: null,
      path: "/learning/detour/edit/:id",
      roles: ["Admin"],
      component: <DetourEdit />,
    },
    {
      label: "QA",
      icon: null,
      path: "/alerts/dataquality/:topic",
      roles: ["Admin", "Expert", "Readonly"],
      component: <DataQualityTopic />,
    },
  ],
};

export function getFilteredMenu(role: Roles, isDeveloper: boolean) {
  const filteredMenu: MenuBase = {};

  // Iterate over each section
  for (const section in menuConfig) {
    if (section !== "NoMenu") {
      if (menuConfig.hasOwnProperty(section)) {
        const items = menuConfig[section as Sections];

        if (items) {
          // Apply filtering to items
          const filteredItems = items.filter((item: MenuItem) =>
            isPageShown(item, role, isDeveloper),
          );

          // If there are filtered items, include this section in the new MenuBase
          if (filteredItems.length > 0) {
            filteredMenu[section as Sections] = filteredItems;
          }
        }
      }
    }
  }

  return filteredMenu;
}

export function getAllMenuItems() {
  return Object.values(menuConfig).flat();
}
